/** @format */
import { ChangeDetectionStrategy, Component, input, InputSignal, ViewEncapsulation } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
    CourseUnit,
    LocalizedString,
    OpenUniversityProduct,
    OtmId,
    StudyVoucherPurchaseActivationInformation,
    UniversityConfig,
} from 'common-typescript/types';
import { map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { ConfigService, NavigationService } from 'sis-common';
import { AppErrorHandler, CourseUnitEntityService, OpenUniversityProductEntityService } from 'sis-components';
import { CART_PURCHASE_WIZARD_STEP_KEYS, STEP } from '../../../constants';

@Component({
    selector: 'app-study-voucher-verify-identity',
    standalone: false,
    templateUrl: './study-voucher-verify-identity.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudyVoucherVerifyIdentityComponent {
    /**
     * Transaction id received from Opin.fi.
     */
    readonly transactionId: InputSignal<string> = input.required<string>();

    /**
     * Activation information for the purchase referred by {@link transactionId}.
     */
    readonly activationInformation: InputSignal<StudyVoucherPurchaseActivationInformation> =
        input.required<StudyVoucherPurchaseActivationInformation>();

    /**
     * Translation keys of the wizard steps to show.
     */
    protected readonly wizardStepNameKeys: readonly string[] = CART_PURCHASE_WIZARD_STEP_KEYS;

    /**
     * Zero-based index of the selected step in {@link wizardStepNameKeys}.
     */
    protected readonly currentWizardStepIndex: number = STEP.CONFIRMATION;

    /**
     * Emits combined product and course unit information of the purchased open university products,
     * ordered by {@link PurchasedStudy.code}.
     */
    protected readonly purchasedStudies$: Observable<readonly PurchasedStudy[]>;

    constructor(
        private readonly _configService: ConfigService,
        private readonly _navigationService: NavigationService,
        private readonly _openUniversityProductEntityService: OpenUniversityProductEntityService,
        private readonly _courseUnitEntityService: CourseUnitEntityService,
        appErrorHandler: AppErrorHandler,
    ) {
        this.purchasedStudies$ = toObservable(this.activationInformation).pipe(
            switchMap(activationInformation => this.getPurchasedStudiesForActivationInformation(activationInformation)),
            appErrorHandler.defaultErrorHandler(),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    /**
     * Redirects the user to Suomi.fi.
     */
    protected proceedToSuomiFi(): void {
        const transactionId: string | undefined = this.transactionId();
        if (!transactionId) {
            return;
        }

        const universityConfig: UniversityConfig = this._configService.get();

        const origin: string = this._navigationService.locationOrigin;
        const path: string = `/student/open-university-cart/study-voucher/${transactionId}/studies-activate-suomi-fi`;
        const targetView: string = `${origin}${path}`;

        /*
        When "suomiFiShibbolethLoginUrl" exists we do suomi.fi authentication trough shibboleth service.
        "dev" environment does not use shibboleth so we go instantly to targetView.
         */
        const redirectUrl: string = universityConfig?.suomiFiShibbolethLoginUrl
            ? `${universityConfig.suomiFiShibbolethLoginUrl}?target=${encodeURIComponent(targetView)}`
            : targetView;

        this._navigationService.navigate(redirectUrl);
    }

    private getPurchasedStudiesForActivationInformation(
        activationInformation: StudyVoucherPurchaseActivationInformation | undefined,
    ): Observable<readonly PurchasedStudy[]> {
        if (!activationInformation?.openUniversityProductIds.length) {
            return of([]);
        }

        return this._openUniversityProductEntityService
            .getByIds(activationInformation.openUniversityProductIds)
            .pipe(
                switchMap((openUniversityProducts: readonly OpenUniversityProduct[]) =>
                    this.getPurchasedStudiesForOpenUniversityProducts(openUniversityProducts),
                ),
            );
    }

    private getPurchasedStudiesForOpenUniversityProducts(
        openUniversityProducts: readonly OpenUniversityProduct[],
    ): Observable<readonly PurchasedStudy[]> {
        if (!openUniversityProducts.length) {
            return of([]);
        }

        const distinctCourseUnitIds: OtmId[] = new Set<OtmId>(
            openUniversityProducts.map(openUniversityProduct => openUniversityProduct.courseUnitId),
        )
            .values()
            .toArray();

        return this._courseUnitEntityService
            .getByIds(distinctCourseUnitIds)
            .pipe(
                map((courseUnits: readonly CourseUnit[]) =>
                    getPurchasedStudiesForOpenUniversityProductsAndCourseUnits(openUniversityProducts, courseUnits),
                ),
            );
    }
}

function createPurchasedStudyInfo(openUniversityProduct: OpenUniversityProduct, courseUnit: CourseUnit): PurchasedStudy {
    return {
        name: courseUnit.name,
        credits: openUniversityProduct.credits,
        code: courseUnit.code,
    };
}

function stringCompare(a: string | null, b: string | null): number {
    if (!a) {
        return b ? 1 : 0;
    }

    if (!b) {
        return -1;
    }

    return a.localeCompare(b);
}

function getPurchasedStudiesForOpenUniversityProductsAndCourseUnits(
    openUniversityProducts: readonly OpenUniversityProduct[],
    courseUnits: readonly CourseUnit[],
): readonly PurchasedStudy[] {
    if (!courseUnits.length) {
        return [];
    }

    const courseUnitMap = new Map<OtmId, CourseUnit>(courseUnits.map(cu => [cu.id, cu]));

    return openUniversityProducts
        .map(openUniversityProduct => ({
            openUniversityProduct,
            courseUnit: courseUnitMap.get(openUniversityProduct.courseUnitId),
        }))
        .filter(openUniversityProductAndCourseUnit => !!openUniversityProductAndCourseUnit.courseUnit)
        .toSorted((a, b) => {
            // sort primarily by course unit code, and use OpenUniversityProduct.id as a tie breaker
            const codeComparison: number = stringCompare(a.courseUnit.code, b.courseUnit.code);
            return codeComparison === 0 ? stringCompare(a.openUniversityProduct.id, b.openUniversityProduct.id) : codeComparison;
        })
        .map(openUniversityProductAndCourseUnit =>
            createPurchasedStudyInfo(
                openUniversityProductAndCourseUnit.openUniversityProduct,
                openUniversityProductAndCourseUnit.courseUnit,
            ),
        );
}

/**
 * This is a combination of a purchased {@link OpenUniversityProduct} and a {@link CourseUnit} referred by it.
 */
export interface PurchasedStudy {
    readonly name: LocalizedString | null;
    readonly credits: number | null;
    readonly code: string | null;
}
